import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

const ActivityLogsTab = () => {
    const Auth = useAuthentication();
    const CurrentUser = useSelector((state) => state.CurrentUser);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        username: '',
        action: '',
        entity: '',
        date_from: '',
        date_to: '',
        search: '',
        method: '',
        response_status: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        actions: [],
        entities: []
    });
    const [pagination, setPagination] = useState({
        limit: 50,
        offset: 0,
        total: 0,
        hasMore: false
    });
    const [expandedLog, setExpandedLog] = useState(null);

    // Filter-Optionen laden
    useEffect(() => {
        fetchFilterOptions();
    }, []);

    // Logs laden
    useEffect(() => {
        fetchLogs();
    }, [filters, pagination.offset]);

    const fetchFilterOptions = async () => {
        try {
            const response = await axios({
                method: "get",
                auth: Auth.getAuthObject(),
                url: SyncConstants["syncBaseURL"] + "logs/filter-options",
                responseType: 'json'
            });
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = {
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, v]) => v !== '')
                ),
                limit: pagination.limit,
                offset: pagination.offset
            };

            const response = await axios({
                method: "get",
                auth: Auth.getAuthObject(),
                url: SyncConstants["syncBaseURL"] + "logs",
                params: params,
                responseType: 'json'
            });

            setLogs(response.data.data || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.meta?.total || 0,
                hasMore: response.data.meta?.has_more || false
            }));
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, offset: 0 })); // Reset offset
    };

    const handleLoadMore = () => {
        setPagination(prev => ({
            ...prev,
            offset: prev.offset + prev.limit
        }));
    };

    const handleReset = () => {
        setFilters({
            username: '',
            action: '',
            entity: '',
            date_from: '',
            date_to: '',
            search: '',
            method: '',
            response_status: ''
        });
        setPagination(prev => ({ ...prev, offset: 0 }));
    };

    const getStatusBadgeClass = (status) => {
        if (status >= 200 && status < 300) return 'badge bg-success';
        if (status >= 300 && status < 400) return 'badge bg-info';
        if (status >= 400 && status < 500) return 'badge bg-warning';
        if (status >= 500) return 'badge bg-danger';
        return 'badge bg-secondary';
    };

    const getActionBadgeClass = (action) => {
        const actionMap = {
            'create': 'badge bg-success',
            'update': 'badge bg-primary',
            'delete': 'badge bg-danger',
            'view': 'badge bg-info',
            'export': 'badge bg-warning',
            'import': 'badge bg-warning',
            'sync': 'badge bg-secondary'
        };
        return actionMap[action] || 'badge bg-secondary';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const JsonViewer = ({ data }) => {
        if (!data) return <span className="text-muted">-</span>;

        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            return (
                <pre className="bg-light p-2 rounded" style={{ maxHeight: '300px', overflow: 'auto', fontSize: '12px' }}>
                    {JSON.stringify(parsed, null, 2)}
                </pre>
            );
        } catch {
            return <span className="text-muted">Invalid JSON</span>;
        }
    };

    return (
        <div>
            <h3 className="mb-4">Activity Logs</h3>

            {/* Filter Section */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Filter</h5>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Username</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={filters.username}
                                onChange={(e) => handleFilterChange('username', e.target.value)}
                                placeholder="Username suchen..."
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Action</label>
                            <select
                                className="form-select form-select-sm"
                                value={filters.action}
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                            >
                                <option value="">Alle</option>
                                {(filterOptions.actions || []).map(action => (
                                    <option key={action} value={action}>{action}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Entity</label>
                            <select
                                className="form-select form-select-sm"
                                value={filters.entity}
                                onChange={(e) => handleFilterChange('entity', e.target.value)}
                            >
                                <option value="">Alle</option>
                                {(filterOptions.entities || []).map(entity => (
                                    <option key={entity} value={entity}>{entity}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Method</label>
                            <select
                                className="form-select form-select-sm"
                                value={filters.method}
                                onChange={(e) => handleFilterChange('method', e.target.value)}
                            >
                                <option value="">Alle</option>
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select form-select-sm"
                                value={filters.response_status}
                                onChange={(e) => handleFilterChange('response_status', e.target.value)}
                            >
                                <option value="">Alle</option>
                                <option value="200">200 OK</option>
                                <option value="201">201 Created</option>
                                <option value="400">400 Bad Request</option>
                                <option value="401">401 Unauthorized</option>
                                <option value="403">403 Forbidden</option>
                                <option value="404">404 Not Found</option>
                                <option value="500">500 Server Error</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Von Datum</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.date_from}
                                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Bis Datum</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.date_to}
                                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Suche</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="Freitextsuche..."
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                className="btn btn-secondary btn-sm w-100"
                                onClick={handleReset}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">
                            Logs ({pagination.total})
                        </h5>
                        {loading && <div className="spinner-border spinner-border-sm" role="status" />}
                    </div>

                    {logs.length === 0 ? (
                        <div className="alert alert-info">Keine Logs gefunden</div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover table-sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '150px' }}>Zeitstempel</th>
                                            <th style={{ width: '120px' }}>User</th>
                                            <th style={{ width: '80px' }}>Action</th>
                                            <th style={{ width: '100px' }}>Entity</th>
                                            <th style={{ width: '80px' }}>Method</th>
                                            <th>Endpoint</th>
                                            <th style={{ width: '80px' }}>Status</th>
                                            <th style={{ width: '80px' }}>Dauer</th>
                                            <th style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log) => (
                                            <React.Fragment key={log.id}>
                                                <tr
                                                    className="cursor-pointer"
                                                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                                >
                                                    <td className="small">{formatDate(log.created_at)}</td>
                                                    <td className="small">{log.username || '-'}</td>
                                                    <td>
                                                        <span className={getActionBadgeClass(log.action)}>
                                                            {log.action}
                                                        </span>
                                                    </td>
                                                    <td className="small">{log.entity}</td>
                                                    <td className="small">
                                                        <span className="badge bg-secondary">{log.method}</span>
                                                    </td>
                                                    <td className="small text-truncate" style={{ maxWidth: '200px' }}>
                                                        {log.endpoint}
                                                    </td>
                                                    <td>
                                                        <span className={getStatusBadgeClass(log.response_status)}>
                                                            {log.response_status}
                                                        </span>
                                                    </td>
                                                    <td className="small">{log.duration_ms ? `${log.duration_ms}ms` : '-'}</td>
                                                    <td>
                                                        <i className={`bi bi-chevron-${expandedLog === log.id ? 'up' : 'down'}`}></i>
                                                    </td>
                                                </tr>
                                                {expandedLog === log.id && (
                                                    <tr>
                                                        <td colSpan="9" className="bg-light">
                                                            <div className="p-3">
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <h6>Details</h6>
                                                                        <table className="table table-sm table-borderless">
                                                                            <tbody>
                                                                                <tr>
                                                                                    <td className="text-muted" style={{ width: '120px' }}>Log-ID:</td>
                                                                                    <td>{log.id}</td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="text-muted">User ID:</td>
                                                                                    <td>{log.user_id || '-'}</td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="text-muted">Entity ID:</td>
                                                                                    <td>{log.entity_id || '-'}</td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="text-muted">IP-Adresse:</td>
                                                                                    <td>{log.ip_address || '-'}</td>
                                                                                </tr>
                                                                                <tr>
                                                                                    <td className="text-muted">User-Agent:</td>
                                                                                    <td className="small" style={{ wordBreak: 'break-all' }}>
                                                                                        {log.user_agent || '-'}
                                                                                    </td>
                                                                                </tr>
                                                                                {log.error_message && (
                                                                                    <tr>
                                                                                        <td className="text-muted">Fehler:</td>
                                                                                        <td className="text-danger">{log.error_message}</td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <h6>Request-Daten (JSON)</h6>
                                                                        <JsonViewer data={log.data} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Load More Button */}
                            {pagination.hasMore && (
                                <div className="text-center mt-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                    >
                                        {loading ? 'Lädt...' : 'Mehr laden'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivityLogsTab;
